import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { signOut as signOutFromMsal } from '../auth/msal';
import { useSession } from '../session';

type NavItem = {
  label: string;
  to: string;
};

type UnifiedHeaderProps = {
  showNav?: boolean;
};

const navBaseClass =
  'inline-flex items-center justify-center h-10 px-4 rounded-lg text-[13px] font-bold tracking-[0.01em]';
const navActiveClass = `${navBaseClass} bg-primary/10 text-primary border border-primary/10`;
const navInactiveClass =
  `${navBaseClass} border border-border bg-white text-ink transition hover:bg-slate-50 hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35`;

const opsActiveClass = `${navBaseClass} bg-amber-100 text-amber-950 border border-amber-200`;
const opsInactiveClass =
  `${navBaseClass} border border-amber-400 bg-amber-50 text-amber-950 transition hover:bg-amber-100 hover:border-amber-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/35`;

export default function UnifiedHeader({ showNav = true }: UnifiedHeaderProps) {
  const location = useLocation();
  const { profile, isActive, hasPhone, hasBilling, isSystemAdmin } = useSession();
  const isRegistered = isActive && hasPhone && hasBilling;
  const shouldShowNav = showNav && isRegistered;
  const navItems = React.useMemo<NavItem[]>(() => {
    const items: NavItem[] = [
      { label: 'Overview', to: '/app' },
      { label: 'Calls', to: '/calls' },
      { label: 'Manage Agents', to: '/agents' },
    ];
    if (isSystemAdmin) {
      items.push({ label: 'Ops', to: '/ops' });
    }
    return items;
  }, [isSystemAdmin]);

  async function handleSignOut() {
    await signOutFromMsal();
  }

  return (
    <header className="sticky top-0 z-10 border-b border-border/90 bg-surfaceAlt/90 backdrop-blur-md">
      <div className="px-4 sm:px-6 lg:px-8 py-3">
        <div className="grid grid-cols-[auto,1fr,auto] items-center gap-4">
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center bg-primary/12 text-primary text-[12px] font-extrabold"
              aria-hidden
            >
              TV
            </div>
            <div className="flex flex-col">
              <span className="text-[18px] font-extrabold tracking-[-0.01em]">TouchVoice</span>
              {profile?.name && (
                <span className="text-[12px] text-muted">
                  Signed in as
                  {' '}
                  {profile.name}
                </span>
              )}
            </div>
          </div>
          {shouldShowNav ? (
            <nav className="flex items-center justify-center gap-2" aria-label="Primary">
              {navItems.map((item) => {
                const isOpsItem = item.to === '/ops';
                const isExactMatch = location.pathname === item.to;
                const isOpsSubpage = isOpsItem && location.pathname.startsWith('/ops/');
                const isActiveItem = isExactMatch || isOpsSubpage;
                if (isActiveItem) {
                  if (isOpsSubpage) {
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        className={opsActiveClass}
                      >
                        {item.label}
                      </Link>
                    );
                  }
                  return (
                    <span
                      key={item.to}
                      className={isOpsItem ? opsActiveClass : navActiveClass}
                      aria-current="page"
                    >
                      {item.label}
                    </span>
                  );
                }
                return (
                  <Link key={item.to} to={item.to} className={isOpsItem ? opsInactiveClass : navInactiveClass}>
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          ) : (
            <div aria-hidden />
          )}
          <div className="flex items-center justify-end gap-2">
            <button type="button" className="tv-btn-primary h-10 px-4" onClick={handleSignOut}>
              Sign out
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
